import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { FlatRoute } from '../domain/route/FlatRoute';
import { Route } from '../domain/route/Route';
import { RouteStatus } from '../domain/route/RouteStatus';
import { Stop } from '../domain/stop/Stop';
import { routeRepository } from '../repositories/route';
import { RetrieveRoutesProps, RouteRepository, TrackPositionProps } from '../repositories/route/RouteRepository';

class RouteService {
  constructor(private routeRepository: RouteRepository) {}

  fetchRoute({ routeId }: { routeId: string }): Promise<Route> {
    return this.routeRepository.fetchRoute({ routeId });
  }

  updateStatus({ status, route }: { status: RouteStatus; route: Route }): Promise<Route> {
    route.status = status;
    return this.routeRepository.updateStatus({ route });
  }

  fetchRoutes({ offset, limit, sort, status }: RetrieveRoutesProps): Promise<PaginatedResult<Route>> {
    return this.routeRepository.fetchRoutes({ offset, limit, sort, status });
  }

  trackPosition({ route, lat, lng, source }: TrackPositionProps): Promise<void> {
    return this.routeRepository.trackPosition({ route, lat, lng, source });
  }

  createStops({ stops, route, optimize }: { stops: Stop[]; route: Route; optimize: boolean }): Promise<Route> {
    return this.routeRepository.createStops({ stops, route, optimize });
  }

  updateStopsOrder({ stops, optimize, route }: { stops: string[]; optimize: boolean; route: Route }): Promise<Route> {
    return this.routeRepository.updateStopsOrder({ stops, optimize, route });
  }

  searchRoutes({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Route>> {
    return this.routeRepository.searchRoutes({
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });
  }

  async searchLastUnfinishedRoute(): Promise<Route | null> {
    const routesPagination = await this.routeRepository.searchRoutes({
      limit: 1,
      sort_by: `plan_execution_date`,
      sort_direction: `desc`,
      predicates: [
        SearchPredicate.create({
          field: `status`,
          operator: `eq`,
          type: `string`,
          value: `in_transit`,
        }),
      ],
    });

    return routesPagination.docs[0] ?? null;
  }

  searchFlatRoutes({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<FlatRoute>> {
    return this.routeRepository.searchFlatRoutes({
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });
  }

  searchUnfinishedRoutes(): Promise<number> {
    return this.routeRepository.searchUnfinishedRoutes();
  }
  endUnfinishedRoutes(): Promise<number> {
    return this.routeRepository.endUnfinishedRoutes();
  }

  createRoute({
    label,
    execution_date,
    stops_ids_to_move,
  }: {
    label?: string;
    execution_date: Date;
    stops_ids_to_move?: string[];
  }): Promise<Route> {
    return this.routeRepository.createRoute({ label, execution_date, stops_ids_to_move });
  }

  updateRoute({ route }: { route: Route }): Promise<Route> {
    return this.routeRepository.updateRoute({ route });
  }

  deleteRoute({ route }: { route: Route }): Promise<void> {
    return this.routeRepository.deleteRoute({ route });
  }
  duplicateRoute({
    label,
    execution_date,
    route_id,
  }: {
    label: string;
    execution_date: Date;
    route_id: string;
  }): Promise<Route> {
    return this.routeRepository.duplicateRoute({ label, execution_date, route_id });
  }
  acceptOptimization({ route_id, log_id }: { route_id: string; log_id: string }): Promise<Route> {
    return this.routeRepository.acceptOptimization({ route_id, log_id });
  }
}

export const routeService = new RouteService(routeRepository);
